import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { MUSIC_TRACK_KIND, parseMusicTrack, type MusicTrack } from '@/lib/musicTypes';
import { useBatchTrackZaps } from '@/hooks/useBatchTrackZaps';

export type SortOrder = 'newest' | 'most-zapped';

interface UseMusicTracksOptions {
  sortOrder?: SortOrder;
  limit?: number;
  relays?: string[];
}

interface TrackWithZapTotal extends MusicTrack {
  totalZaps: number;
}

export function useMusicTracks(options: UseMusicTracksOptions = {}) {
  const { sortOrder = 'newest', limit = 50, relays } = options;
  const { nostr } = useNostr();

  // Fetch tracks first
  const tracksQuery = useQuery({
    queryKey: ['music-tracks', limit, relays],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Connect to multiple relays for better discovery
      const relayList = relays || [
        'wss://relay.ditto.pub',
        'wss://relay.nostr.band',
        'wss://relay.damus.io',
        'wss://nos.lol',
      ];

      const relayGroup = nostr.group(relayList);

      const events = await relayGroup.query(
        [{ kinds: [MUSIC_TRACK_KIND], '#t': ['music'], limit }],
        { signal }
      );

      // Parse and filter valid tracks
      const tracks = events
        .map(parseMusicTrack)
        .filter((track): track is MusicTrack => track !== null);

      // DEDUPLICATION: If multiple users published events with the same d-tag,
      // keep only the OLDEST version (original creator) per d-tag.
      // This prevents "track stealing" where someone republishes another's track.
      const tracksByDTag = new Map<string, MusicTrack>();

      for (const track of tracks) {
        const existing = tracksByDTag.get(track.id);

        if (!existing) {
          // First track with this d-tag
          tracksByDTag.set(track.id, track);
        } else if (track.createdAt < existing.createdAt) {
          // This track is older (original), replace the newer one
          tracksByDTag.set(track.id, track);
        }
        // If existing is older, keep it and ignore this duplicate
      }

      // Convert back to array
      const dedupedTracks = Array.from(tracksByDTag.values());

      // Sort by newest first for initial display
      dedupedTracks.sort((a, b) => b.createdAt - a.createdAt);

      return dedupedTracks;
    },
  });

  // Create a-tags for zap queries
  const aTags = tracksQuery.data?.map((track) => `${MUSIC_TRACK_KIND}:${track.pubkey}:${track.id}`) || [];

  // Fetch zap data separately (only when we have tracks)
  const { data: zapTotals, isLoading: isLoadingZaps } = useBatchTrackZaps({
    aTags,
    relays,
    enabled: aTags.length > 0,
  });

  // Combine tracks with zap data and sort
  const data = tracksQuery.data ? (() => {
    if (sortOrder === 'newest') {
      // Already sorted by newest
      return tracksQuery.data;
    } else if (sortOrder === 'most-zapped') {
      // Add zap totals and sort
      const tracksWithZaps: TrackWithZapTotal[] = tracksQuery.data.map((track) => {
        const aTag = `${MUSIC_TRACK_KIND}:${track.pubkey}:${track.id}`;
        return {
          ...track,
          totalZaps: zapTotals?.get(aTag) || 0,
        };
      });

      // Sort by total zaps descending
      tracksWithZaps.sort((a, b) => b.totalZaps - a.totalZaps);

      return tracksWithZaps;
    }
    return tracksQuery.data;
  })() : undefined;

  return {
    data,
    isLoading: tracksQuery.isLoading,
    // For most-zapped, show as loading if zaps are still being fetched
    isPending: tracksQuery.isPending || (sortOrder === 'most-zapped' && isLoadingZaps),
    error: tracksQuery.error,
  };
}
